import { useForm } from '@tanstack/react-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';

import { Button } from '@/components/button/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog/dialog';
import { Input } from '@/components/input/input';
import { Label } from '@/components/label/label';
import { Switch } from '@/components/switch/switch';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import { GET_LIST_ROLE_QUERY_KEY } from '@/features/roles/api/get-list-role.api';
import {
  GET_ROLE_QUERY_KEY,
  getRoleQueryOptions,
} from '@/features/roles/api/get-role.api';
import {
  UpdateRoleSchema,
  updateRoleMutationOptions,
} from '@/features/roles/api/update-role.api';
import { getListPermissionQueryOptions } from '@/features/roles/api/get-list-permission.api';
import {
  getPermissionDescription,
  getPermissionLabel,
} from '@/features/roles/utils/permission-description';
import type { Role } from '@/features/roles/types/roles.type';

export interface EditRoleDialogProps extends BaseDialogProps {
  roleId: string;
}

export default function EditRoleDialog({
  dialog,
  roleId,
}: EditRoleDialogProps) {
  const { closeEditRoleDialog } = useV2DialogContext();

  const getRoleQuery = useQuery(getRoleQueryOptions({ roleId }));
  const role = getRoleQuery.data?.result;

  const getListPermissionQuery = useQuery(getListPermissionQueryOptions());
  const permissions = getListPermissionQuery.data?.result ?? [];

  const isLoading = getRoleQuery.isPending || getListPermissionQuery.isPending;

  return (
    <Dialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState) {
          closeEditRoleDialog();
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Role</DialogTitle>
          <DialogDescription>
            Perbarui data role di bawah ini.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex min-h-40 items-center justify-center text-sm text-muted-foreground">
            Memuat data role...
          </div>
        ) : getRoleQuery.isError || !role ? (
          <p className="text-sm text-destructive">
            Gagal memuat data role. Silakan coba lagi.
          </p>
        ) : (
          <EditRoleForm
            roleId={roleId}
            role={role}
            permissions={permissions}
            onClose={closeEditRoleDialog}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface EditRoleFormProps {
  roleId: string;
  role: Role;
  permissions: string[];
  onClose: () => void;
}

function EditRoleForm({
  roleId,
  role,
  permissions,
  onClose,
}: EditRoleFormProps) {
  const queryClient = useQueryClient();

  const updateRoleMutation = useMutation({
    ...updateRoleMutationOptions(),
    onSuccess: () => {
      toast.success('Role berhasil diperbarui');
      queryClient.invalidateQueries({ queryKey: [GET_LIST_ROLE_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [GET_ROLE_QUERY_KEY, { roleId }],
      });
      onClose();
    },
    onError: error => {
      const message =
        error instanceof AxiosError
          ? ((error.response?.data as { message?: string })?.message ??
            error.message)
          : 'Gagal memperbarui role';
      toast.error(message);
    },
  });

  const form = useForm({
    defaultValues: {
      role: role.role,
      permissions: role.permissions.map(permission => permission.name),
    },
    onSubmit: async ({ value }) => {
      const parsed = UpdateRoleSchema.safeParse(value);
      if (!parsed.success) {
        toast.error(parsed.error.issues[0]?.message ?? 'Data role tidak valid');
        return;
      }
      await updateRoleMutation.mutateAsync({
        roleId,
        data: parsed.data,
      });
    },
  });

  const isPending = updateRoleMutation.isPending;

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={e => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <form.Field name="role">
        {field => (
          <div className="grid gap-2">
            <Label htmlFor={field.name}>Nama Role</Label>
            <Input
              id={field.name}
              placeholder="Masukkan Nama Role"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={e => field.handleChange(e.target.value)}
            />
          </div>
        )}
      </form.Field>

      <form.Field name="permissions">
        {field => (
          <div className="flex flex-col gap-3">
            <Label>Permission</Label>
            {permissions.length === 0 ? (
              <div className="rounded-md border border-border p-4 text-center text-sm text-muted-foreground">
                Tidak ada permission tersedia
              </div>
            ) : (
              <div className="flex flex-col divide-y divide-border rounded-md border border-border">
                {permissions.map(permission => {
                  const isChecked =
                    field.state.value?.includes(permission) ?? false;
                  return (
                    <div
                      key={permission}
                      className="flex items-start gap-3 p-3"
                    >
                      <Switch
                        checked={isChecked}
                        onCheckedChange={checked => {
                          const current = field.state.value ?? [];
                          if (checked) {
                            field.handleChange([...current, permission]);
                          } else {
                            field.handleChange(
                              current.filter(p => p !== permission)
                            );
                          }
                        }}
                      />
                      <div className="flex flex-1 flex-col gap-0.5">
                        <p className="text-sm font-medium text-foreground">
                          {getPermissionLabel(permission)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {getPermissionDescription(permission)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </form.Field>

      <DialogFooter className="pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={isPending}
        >
          Batal
        </Button>
        <form.Subscribe selector={state => [state.canSubmit]}>
          {([canSubmit]) => (
            <Button type="submit" disabled={!canSubmit || isPending}>
              {isPending ? 'Menyimpan...' : 'Simpan'}
            </Button>
          )}
        </form.Subscribe>
      </DialogFooter>
    </form>
  );
}
